import type { PermissionSeed, PermissionSeedMeta } from "./types";

/**
 * Application permissions derived from SPA pages, API routes (`docs/API.md`),
 * and SPA keys in `lib/auth/rbac-config.ts` (`PERMISSIONS`).
 *
 * Import in your API service seed script, e.g.:
 *   import { PERMISSION_SEEDS } from "./permissions.seed";
 */
export const PERMISSION_SEEDS: PermissionSeed[] = [
  // ─── Dashboard (`/dashboard`, GET /v1/dashboard/*) ───────────────────────
  { name: "View dashboard stats", resource: "dashboard", action: "read" },
  { name: "View dashboard activity", resource: "dashboard", action: "activity" },

  // ─── Studies (`/studies`, POST /v1/videos/upload) ───────────────────────
  // { name: "View clean room", resource: "clean_room", action: "read" },
  { name: "Upload videos", resource: "videos", action: "upload" },

  // ─── Studies list (`/studies`, GET /v1/history) ──────────────────────────
  { name: "List studies", resource: "history", action: "read" },
  // { name: "Download log files from studies", resource: "history", action: "download" },
  // { name: "Download deviation files from studies", resource: "history", action: "download_deviation" },

  // ─── Logs (`/logs`, GET /v1/audit-trail, /api/audit-logs) ────────────────
  { name: "List logs", resource: "audit_trail", action: "read" },
  { name: "View log detail", resource: "audit_trail", action: "detail" },

  // ─── Users (`/users`, GET /v1/users) ─────────────────────────────────────────
  { name: "List users", resource: "users", action: "read" },
  { name: "Create user", resource: "users", action: "create" },
  { name: "Update user", resource: "users", action: "update" },
  { name: "Update user status", resource: "users", action: "status" },
  { name: "Delete user", resource: "users", action: "delete" },

  // ─── Roles (`/roles`, GET /v1/roles) ─────────────────────────────────────
  { name: "List roles", resource: "roles", action: "read" },
  { name: "Create role", resource: "roles", action: "create" },
  { name: "Update role", resource: "roles", action: "update" },
  { name: "Delete role", resource: "roles", action: "delete" },
  // { name: "Manage roles", resource: "roles", action: "manage" },

  // ─── Frames (`/frame`, GET/PATCH /v1/alerts) ───────────────────────────────
  { name: "List frames", resource: "alerts", action: "read" },
  { name: "View frame details", resource: "alerts", action: "detail" },
  { name: "Update frame", resource: "alerts", action: "update" },

  // ─── Screenshots (`/screenshot`, GET /v1/screenshots) ──────────────────────
  // { name: "List screenshots", resource: "screenshots", action: "read" },
  { name: "View screenshot details", resource: "screenshots", action: "detail" },
  { name: "Update screenshot", resource: "screenshots", action: "update" },

  // ─── Masters hierarchy (`/masters`, equipment, categories, interventions) ─
  { name: "List masters", resource: "masters", action: "read" },
  { name: "Create master", resource: "masters", action: "create" },
  { name: "Update master", resource: "masters", action: "update" },
  { name: "Delete master", resource: "masters", action: "delete" },
  { name: "List equipment", resource: "equipment", action: "read" },
  { name: "Create equipment", resource: "equipment", action: "create" },
  { name: "Update equipment", resource: "equipment", action: "update" },
  { name: "Delete equipment", resource: "equipment", action: "delete" },
  {
    name: "List intervention categories",
    resource: "intervention_categories",
    action: "read",
  },
  {
    name: "Create intervention category",
    resource: "intervention_categories",
    action: "create",
  },
  {
    name: "Update intervention category",
    resource: "intervention_categories",
    action: "update",
  },
  {
    name: "Delete intervention category",
    resource: "intervention_categories",
    action: "delete",
  },
  { name: "List interventions", resource: "interventions", action: "read" },
  { name: "Create intervention", resource: "interventions", action: "create" },
  { name: "Update intervention", resource: "interventions", action: "update" },
  { name: "Delete intervention", resource: "interventions", action: "delete" },

  // ─── Reports (`/reports`, POST /reports/smoke-study/pdf) ─────────────────
  { name: "View reports", resource: "reports", action: "read" },
  { name: "Download reports", resource: "reports", action: "download" },

  // ─── Profile (`/profile`) ──────────────────────────────────────────────────
  { name: "View own profile", resource: "profile", action: "read" },
  { name: "Update own profile", resource: "profile", action: "update" },
];

/**
 * Same data with optional SPA permission keys for keeping client RBAC in sync.
 */
export const PERMISSION_SEEDS_WITH_SPA_KEYS: PermissionSeedMeta[] = [
  { name: "View dashboard stats", resource: "dashboard", action: "read", spaKey: "view:dashboard" },
  {
    name: "View dashboard activity",
    resource: "dashboard",
    action: "activity",
    spaKey: "view:dashboard",
  },
  // { name: "View clean room", resource: "clean_room", action: "read", spaKey: "view:history" },
  { name: "Upload videos", resource: "videos", action: "upload", spaKey: "upload:videos" },
  { name: "List studies", resource: "history", action: "read", spaKey: "view:history" },
  // {
  //   name: "Download log files from studies",
  //   resource: "history",
  //   action: "download",
  //   spaKey: "download:audit-files",
  // },
  // {
  //   name: "Download deviation files from studies",
  //   resource: "history",
  //   action: "download_deviation",
  //   spaKey: "download:audit-files",
  // },
  {
    name: "List logs",
    resource: "audit_trail",
    action: "read",
    spaKey: "view:audit-trail",
  },
  {
    name: "View log detail",
    resource: "audit_trail",
    action: "detail",
    spaKey: "view:audit-trail",
  },
  { name: "List users", resource: "users", action: "read", spaKey: "view:users" },
  { name: "Create user", resource: "users", action: "create", spaKey: "view:users" },
  { name: "Update user", resource: "users", action: "update", spaKey: "view:users" },
  { name: "Update user status", resource: "users", action: "status", spaKey: "view:users" },
  { name: "Delete user", resource: "users", action: "delete", spaKey: "view:users" },
  { name: "List roles", resource: "roles", action: "read", spaKey: "view:roles" },
  { name: "Create role", resource: "roles", action: "create", spaKey: "view:roles" },
  { name: "Update role", resource: "roles", action: "update", spaKey: "view:roles" },
  { name: "Delete role", resource: "roles", action: "delete", spaKey: "view:roles" },
  // { name: "Manage roles", resource: "roles", action: "manage", spaKey: "view:roles" },
  { name: "List frames", resource: "alerts", action: "read", spaKey: "view:alerts" },
  { name: "View frame details", resource: "alerts", action: "detail", spaKey: "view:alerts" },
  { name: "Update frame", resource: "alerts", action: "update", spaKey: "review:alerts" },
  // {
  //   name: "List screenshots",
  //   resource: "screenshots",
  //   action: "read",
  //   spaKey: "view:screenshots",
  // },
  {
    name: "View screenshot details",
    resource: "screenshots",
    action: "detail",
    spaKey: "view:screenshots",
  },
  {
    name: "Update screenshot",
    resource: "screenshots",
    action: "update",
    spaKey: "review:screenshots",
  },
  { name: "List masters", resource: "masters", action: "read", spaKey: "view:masters" },
  { name: "Create master", resource: "masters", action: "create", spaKey: "create:masters" },
  { name: "Update master", resource: "masters", action: "update", spaKey: "update:masters" },
  { name: "Delete master", resource: "masters", action: "delete", spaKey: "delete:masters" },
  { name: "List equipment", resource: "equipment", action: "read", spaKey: "view:masters" },
  { name: "Create equipment", resource: "equipment", action: "create", spaKey: "create:equipment" },
  { name: "Update equipment", resource: "equipment", action: "update", spaKey: "update:equipment" },
  { name: "Delete equipment", resource: "equipment", action: "delete", spaKey: "delete:equipment" },
  {
    name: "List intervention categories",
    resource: "intervention_categories",
    action: "read",
    spaKey: "view:masters",
  },
  {
    name: "Create intervention category",
    resource: "intervention_categories",
    action: "create",
    spaKey: "create:intervention-categories",
  },
  {
    name: "Update intervention category",
    resource: "intervention_categories",
    action: "update",
    spaKey: "update:intervention-categories",
  },
  {
    name: "Delete intervention category",
    resource: "intervention_categories",
    action: "delete",
    spaKey: "delete:intervention-categories",
  },
  {
    name: "List interventions",
    resource: "interventions",
    action: "read",
    spaKey: "view:masters",
  },
  {
    name: "Create intervention",
    resource: "interventions",
    action: "create",
    spaKey: "create:interventions",
  },
  {
    name: "Update intervention",
    resource: "interventions",
    action: "update",
    spaKey: "update:interventions",
  },
  {
    name: "Delete intervention",
    resource: "interventions",
    action: "delete",
    spaKey: "delete:interventions",
  },
  { name: "View reports", resource: "reports", action: "read", spaKey: "view:reports" },
  {
    name: "Download reports",
    resource: "reports",
    action: "download",
    spaKey: "download:reports",
  },
  { name: "View own profile", resource: "profile", action: "read" },
  { name: "Update own profile", resource: "profile", action: "update" },
];

/** Stable code for DB unique constraints: `{resource}:{action}`. */
export function permissionCode(seed: PermissionSeed): string {
  return `${seed.resource}:${seed.action}`;
}
